/**
 * Wellness API Client
 *
 * Axios instance for wellness endpoints with automatic token expiration
 * checking and refresh handling. Only affects /wellness/* routes.
 */

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useWellnessStore } from '@/stores/wellness-store';
import { refreshWellnessTokens } from '@/features/wellness/services/tokenRefresh';
import { handleWellnessAuthFailure } from '@/features/wellness/utils/authRedirect';
import { extractErrorCode } from '@/utils/errorUtils';

const API_URL = import.meta.env.VITE_REACT_APP_API_URL || '';

// Refresh lock to prevent concurrent refresh requests
let refreshPromise: Promise<string | null> | null = null;

/**
 * Ensure we have a valid idToken, refreshing if necessary
 * @returns Valid idToken or null if refresh failed
 */
const ensureValidToken = async (): Promise<string | null> => {
  const { tokens, isIdTokenExpired, isRefreshTokenExpired } = useWellnessStore.getState();

  if (!tokens?.idToken) {
    return null;
  }

  // If idToken is not expired, return it
  if (!isIdTokenExpired()) {
    return tokens.idToken;
  }

  // idToken is expired, check if we can refresh
  const refreshToken = tokens.refreshToken;
  if (isRefreshTokenExpired() || !refreshToken) {
    // No refresh token (e.g. support session) or refresh expired — logout
    console.warn('[wellnessApiClient] Both tokens expired, logging out');
    handleWellnessAuthFailure('Session expired. Please sign in again.');
    return null;
  }

  // Refresh token is still valid, refresh tokens
  // Use lock to prevent concurrent refresh requests
  if (refreshPromise) {
    // Wait for existing refresh to complete
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const newTokens = await refreshWellnessTokens(refreshToken);
      if (newTokens?.idToken) {
        return newTokens.idToken;
      }
      // Refresh failed, logout
      handleWellnessAuthFailure('Token refresh failed. Please sign in again.');
      return null;
    } catch (error) {
      console.error('[wellnessApiClient] Error during token refresh:', error);
      handleWellnessAuthFailure('Token refresh failed. Please sign in again.');
      return null;
    } finally {
      // Clear refresh promise after completion
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

/**
 * Create wellness API client with token management interceptors
 */
export const createWellnessApiClient = (): AxiosInstance => {
  const apiClient = axios.create({
    baseURL: API_URL,
    headers: {
      'Content-Type': 'application/json',
      'tm-front-version': '4',
    },
    timeout: 19000, // 19 second timeout
    withCredentials: false,
  });

  // Request interceptor: Check token expiration and refresh if needed
  apiClient.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      // Try to get a valid token, but allow requests to proceed without it for public endpoints
      const idToken = await ensureValidToken();

      if (idToken) {
        config.headers.Authorization = `Bearer ${idToken}`;
      }
      // If no token is available, allow the request to proceed without Authorization header
      // This enables public endpoints like /self/subscription/getproducts to work without authentication

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor: Handle 403 errors from API Gateway authorizer
  apiClient.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      // Handle 422 business logic errors
      // For wellness routes, all 422 errors (subscription-related) should navigate to upgrade page
      // Don't show dialog - let mutation handlers handle navigation and message display
      if (error.response?.status === 422) {
        // Extract error code from response
        // The code will be translated to a user-friendly message in components that have i18n access
        const errorCode = extractErrorCode(error);
        
        // Store error code in wellness store (will be translated in components with i18n access)
        // If no code found, store a fallback message
        const { setSubscriptionErrorMessage } = useWellnessStore.getState();
        if (errorCode) {
          // Store the code - it will be translated when displayed
          setSubscriptionErrorMessage(errorCode);
        } else {
          // Fallback if code is not present (backward compatibility)
          const errorData = error.response.data as { message?: string } | undefined;
          setSubscriptionErrorMessage(errorData?.message || 'A subscription is required to perform this action.');
        }
        
        // Don't show dialog - mutation handlers will navigate to /wellness/upgrade
        return Promise.reject(error);
      }

      // Handle 403 errors from API Gateway authorizer
      if (error.response?.status === 403) {
        const errorData = error.response.data as
          | { message?: string; reason?: string; error?: string }
          | undefined;

        const reason = errorData?.reason;

        // If request was already retried, don't retry again
        if (originalRequest._retry) {
          handleWellnessAuthFailure('Authentication failed. Please sign in again.');
          return Promise.reject(error);
        }

        // Handle TOKEN_EXPIRED - attempt refresh and retry
        if (reason === 'TOKEN_EXPIRED') {
          originalRequest._retry = true;

          try {
            const { tokens, isRefreshTokenExpired } = useWellnessStore.getState();
            const rt = tokens?.refreshToken;

            // Check if refresh token is still valid
            if (isRefreshTokenExpired() || !rt) {
              // Refresh token expired, logout
              handleWellnessAuthFailure('Session expired. Please sign in again.');
              return Promise.reject(error);
            }

            // Attempt to refresh tokens
            const newTokens = await refreshWellnessTokens(rt);

            if (newTokens?.idToken) {
              // Update Authorization header with new token
              originalRequest.headers.Authorization = `Bearer ${newTokens.idToken}`;
              // Retry original request
              return apiClient(originalRequest);
            } else {
              // Refresh failed, logout
              handleWellnessAuthFailure('Token refresh failed. Please sign in again.');
              return Promise.reject(error);
            }
          } catch (refreshError) {
            console.error('[wellnessApiClient] Error refreshing token on 403:', refreshError);
            handleWellnessAuthFailure('Token refresh failed. Please sign in again.');
            return Promise.reject(error);
          }
        }

        // Handle invalid token errors - logout immediately
        if (
          reason === 'INVALID_TOKEN' ||
          reason === 'INVALID_SIGNATURE' ||
          reason === 'TOKEN_VALIDATION_FAILED' ||
          reason === 'MISSING_AUTHORIZATION_HEADER'
        ) {
          handleWellnessAuthFailure('Invalid authentication token. Please sign in again.');
          return Promise.reject(error);
        }

        // Handle other authorization errors (e.g., ROUTE_NOT_ALLOWED, ROLE_MISMATCH)
        // These are not token expiration issues, so don't attempt refresh
        console.warn('[wellnessApiClient] Authorization denied:', reason, errorData?.error);
        return Promise.reject(error);
      }

      // Handle 401 errors (from non-authorizer endpoints, e.g., refresh endpoint)
      if (error.response?.status === 401) {
        const errorData = error.response.data as { code?: string; message?: string } | undefined;
        const code = errorData?.code;

        // If this is from the refresh endpoint, tokens are expired
        if (code === 'REFRESH_TOKEN_EXPIRED' || code === 'REFRESH_TOKEN_INVALID') {
          handleWellnessAuthFailure('Session expired. Please sign in again.');
          return Promise.reject(error);
        }

        // Other 401 errors
        handleWellnessAuthFailure('Authentication failed. Please sign in again.');
        return Promise.reject(error);
      }

      // Re-throw other errors
      return Promise.reject(error);
    }
  );

  return apiClient;
};

// Export a singleton instance
export const wellnessApiClient = createWellnessApiClient();

